import { useState, useEffect } from 'react';
import { getTicketById, Ticket, deleteTicket, updateTicket } from '../../services/ticketService';
import { listApontamentos, Apontamento } from '../../services/apontamentoService';
import { Loader2, ArrowLeft, Edit, Trash2, Plus, CheckCircle } from 'lucide-react';
import { ApontamentoForm } from './ApontamentoForm';
import { ApontamentoList } from './ApontamentoList';
import { RelatorioButton } from './RelatorioButton';
import { FaturamentoButton } from './FaturamentoButton';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

interface TicketDetailsProps {
  ticketId: string;
  onGoBack: () => void;
  onEdit: (ticketId: string) => void;
}

export const TicketDetails = ({ ticketId, onGoBack, onEdit }: TicketDetailsProps) => {

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [apontamentos, setApontamentos] = useState<Apontamento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showApontamentoForm, setShowApontamentoForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Função de fallback para onGoBack
  const handleGoBack = () => {
    if (typeof onGoBack === 'function') {
      onGoBack();
    } else {
      console.error('onGoBack não é uma função válida');
      // Fallback: tentar navegar para a lista de atendimentos
      window.history.back();
    }
  };

  useEffect(() => {
    if (ticketId) {
      loadData();
    }
  }, [ticketId]);

  const loadData = async () => {
    if (!ticketId) return;

    try {
      setLoading(true);
      setError(null);

      const [ticketData, apontamentosData] = await Promise.all([
        getTicketById(ticketId),
        listApontamentos({ ticketId }),
      ]);

      // Ao "Atender" um ticket ainda ABERTO, inicia o atendimento automaticamente
      if (ticketData.status === 'ABERTO') {
        await updateTicket(ticketId, { status: 'EM_ANDAMENTO' });
        ticketData.status = 'EM_ANDAMENTO';
      }

      setTicket(ticketData);
      // Exibe apenas apontamentos de trabalho (horas); interações com o cliente
      // (respostas, solicitações, aprovações, rejeições) ficam na timeline.
      setApontamentos(
        apontamentosData.filter(
          (a) => !/^\[(RESPOSTA|SOLICITAÇÃO|APROVAÇÃO|REJEIÇÃO)/.test(a.descricao || '')
        )
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar dados');
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!ticketId) return;

    try {
      setDeleting(true);
      await deleteTicket(ticketId);
      onGoBack();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao excluir ticket');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const getPrioridadeColor = (prioridade: string) => {
    switch (prioridade) {
      case 'ALTA':
        return 'border-transparent bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'MEDIA':
        return 'border-transparent bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'BAIXA':
        return 'border-transparent bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default:
        return 'border-transparent bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ABERTO':
        return 'border-transparent bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'EM_ANDAMENTO':
        return 'border-transparent bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'AGUARDANDO_CLIENTE':
        return 'border-transparent bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'AGUARDANDO_APROVACAO':
        return 'border-transparent bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      case 'RESOLVIDO':
        return 'border-transparent bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'FECHADO':
        return 'border-transparent bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      default:
        return 'border-transparent bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const formatDate = (dateString: string) => {
    // Extrai apenas YYYY-MM-DD da string ISO, ignora timezone
    const datePart = dateString.substring(0, 10);
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Ticket não encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={handleGoBack}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{ticket.codigo}</h1>
            <p className="text-sm text-muted-foreground mt-1">{ticket.titulo}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <RelatorioButton ticketId={ticket.id} status={ticket.status} />
          <Button variant="outline" onClick={() => onEdit(ticket.id)}>
            <Edit />
            Editar
          </Button>
          <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 />
            Excluir
          </Button>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Banner de Aprovação */}
      {ticket.status === 'AGUARDANDO_APROVACAO' && !ticket.aprovado && (
        <div className="bg-muted/50 border border-border rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-muted-foreground" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                ⏸️ Ticket Aguardando Aprovação
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Este ticket possui estimativas e precisa ser aprovado antes de iniciar a execução.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {ticket.horasEstimadas && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Horas Estimadas</p>
                    <p className="text-lg font-semibold text-foreground">
                      {ticket.horasEstimadas}h
                    </p>
                  </div>
                )}
                {ticket.valorEstimado && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Valor Estimado</p>
                    <p className="text-lg font-semibold text-foreground">
                      R$ {ticket.valorEstimado.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>

              {(ticket.apontamentos || []).filter((a: any) => a.tipoHora !== 'PROJETO' && !a.faturado).length > 0 && (
                <div className="mb-4 space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Horas para aprovação:</p>
                  {(ticket.apontamentos || [])
                    .filter((a: any) => a.tipoHora !== 'PROJETO' && !a.faturado)
                    .map((a: any) => (
                      <div key={a.id} className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg p-3 border border-border">
                        <span className="text-sm font-medium text-foreground">
                          {a.tipoHora} — {Math.floor(a.minutosTotais / 60)}h{a.minutosTotais % 60}m
                        </span>
                        <span className="text-xs text-muted-foreground truncate max-w-xs">{a.descricao}</span>
                        <span className="text-sm font-semibold text-foreground ml-auto">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                            (a.minutosTotais / 60) * Number(a.valorHora || 0)
                          )}
                        </span>
                      </div>
                    ))}
                </div>
              )}

              <div className="bg-muted rounded-lg p-3 border border-border">
                <p className="text-sm font-medium text-muted-foreground">
                  Aguardando aprovação do cliente
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  O atendimento fica bloqueado até que o cliente aprove a demanda no portal.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Informações do Ticket */}
      <div className="bg-card border border-border rounded-lg shadow p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Informações do Ticket</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground">Cliente</label>
            <p className="text-foreground">
              {ticket.client.nomeFantasia || ticket.client.razaoSocial}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Data de Abertura</label>
            <p className="text-foreground">{formatDate(ticket.createdAt)}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Prioridade</label>
            <div>
              <Badge variant="outline" className={getPrioridadeColor(ticket.prioridade)}>
                {ticket.prioridade}
              </Badge>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Status</label>
            <div>
              <Badge variant="outline" className={getStatusColor(ticket.status)}>
                {ticket.status.replace(/_/g, ' ')}
              </Badge>
            </div>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-muted-foreground">Descrição</label>
          <p className="text-foreground whitespace-pre-wrap">{ticket.descricao}</p>
        </div>

        {/* Estimativas (se houver) */}
        {(ticket.horasEstimadas || ticket.valorEstimado) && (
          <div className="pt-4 border-t border-border">
            <label className="text-sm font-medium text-muted-foreground mb-3 block">
              Estimativas
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ticket.horasEstimadas && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Horas Estimadas</p>
                  <p className="text-lg font-semibold text-foreground">
                    {ticket.horasEstimadas}h
                  </p>
                </div>
              )}
              {ticket.valorEstimado && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Valor Estimado</p>
                  <p className="text-lg font-semibold text-foreground">
                    R$ {ticket.valorEstimado.toFixed(2)}
                  </p>
                </div>
              )}
            </div>
            {ticket.aprovado && ticket.aprovadoEm && (
              <div className="mt-3 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <CheckCircle className="w-4 h-4" />
                <span>
                  Aprovado em {formatDate(ticket.aprovadoEm)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Apontamentos */}
      <div className="bg-card border border-border rounded-lg shadow p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            Apontamentos ({apontamentos.length})
          </h2>
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowApontamentoForm(true)}>
              <Plus />
              Novo Apontamento
            </Button>
            <FaturamentoButton clientId={ticket.clientId} onSuccess={loadData} />
          </div>
        </div>

        <ApontamentoList
          apontamentos={apontamentos}
          onUpdate={loadData}
          onDelete={loadData}
        />
      </div>

      {/* Modal de Apontamento */}
      {showApontamentoForm && (
        <ApontamentoForm
          ticketId={ticket.id}
          onClose={() => setShowApontamentoForm(false)}
          onSuccess={() => {
            setShowApontamentoForm(false);
            loadData();
          }}
        />
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Confirmar Exclusão
            </h3>
            <p className="text-muted-foreground mb-6">
              Tem certeza que deseja excluir este ticket? Esta ação não pode ser desfeita.
            </p>
            {apontamentos.length > 0 && (
              <p className="text-red-600 dark:text-red-400 text-sm mb-6">
                ⚠️ Este ticket possui {apontamentos.length} apontamento(s) registrado(s).
              </p>
            )}
            <div className="flex items-center justify-end gap-4">
              <Button
                onClick={() => setShowDeleteConfirm(false)}
                variant="outline"
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting}
                variant="destructive"
              >
                {deleting && <Loader2 className="animate-spin" />}
                Excluir
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
